import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  optionsSelector,
  totalSelector,
  pageSelector,
  noMoreSelector,
  companiesSelector,
  companySelector,
  companyIdSelector,
} from './store/selector';
import { companiesApiService } from './services/api';
import {
  companyClearAction,
  getCompaniesFailureApiAction,
  getCompaniesInitApiAction,
  getCompaniesSuccessApiAction,
  putCompanyFailuireApiAction,
  putCompanyInitApiAction,
  putCompanySuccessApiAction,
  setOptionsAction,
  resetOptionsAction,
  postCompanyInitApiAction,
  postCompanySuccessApiAction,
  postCompanyFailuireApiAction,
  increasePageAction,
  deleteCompanyInitApiAction,
  deleteCompanySuccessApiAction,
  deleteCompanyFailuireApiAction,
  setCompanyAction,
  setCompanyIdAction,
} from './store/reducer';
import { GetCompaniesParams } from './interfaces/get-companies';
import { PutCompanyPayloadRequest } from './interfaces/put-company';
import { PostCompanyPayloadRequest } from './interfaces/post-company';
import { Company } from '../../interfaces/company.interface';

export function useCompanyCore() {
  const companies = useSelector(companiesSelector);
  const companyId = useSelector(companyIdSelector);
  const company = useSelector(companySelector);
  const total = useSelector(totalSelector);
  const page = useSelector(pageSelector);
  const noMore = useSelector(noMoreSelector);
  const options = useSelector(optionsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getCompanies = useCallback(
    async (params: GetCompaniesParams, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(getCompaniesInitApiAction());
        const response = await companiesApiService.getCompanies(params);
        dispatch(getCompaniesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(getCompaniesFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const postCompany = useCallback(
    async (
      body: PostCompanyPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postCompanyInitApiAction());
        await companiesApiService.postCompany(body);
        dispatch(postCompanySuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postCompanyFailuireApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const putCompany = useCallback(
    async (
      id: number,
      body: PutCompanyPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putCompanyInitApiAction());
        await companiesApiService.putCompany(id, body);
        dispatch(putCompanySuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(putCompanyFailuireApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const deleteCompany = useCallback(
    async (id: number, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(deleteCompanyInitApiAction());
        await companiesApiService.deleteCompany(id);
        dispatch(deleteCompanySuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteCompanyFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setCompany = useCallback((company: Company) => {
    dispatch(setCompanyAction(company));
  }, [dispatch]);

  const setCompanyId = useCallback((id: number) => {
    dispatch(setCompanyIdAction(id));
  }, [dispatch]);

  const setOptions = useCallback((options: GetCompaniesParams) => {
    dispatch(setOptionsAction(options));
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const increasePage = useCallback(() => {
    dispatch(increasePageAction());
  }, [dispatch]);

  const clearCompany = useCallback(() => {
    dispatch(companyClearAction());
  }, [dispatch]);

  return {
    companies,
    companyId,
    company,
    options,
    total,
    page,
    noMore,
    errorCode,
    setCompanyId,
    getCompanies,
    postCompany,
    putCompany,
    deleteCompany,
    setCompany,
    setOptions,
    increasePage,
    resetOptions,
    clearCompany,
  };
}
